<script setup lang="ts">
import { computed, ref } from "vue";
import {
	IDataTableColumns,
	IDataTableDataTypes,
	IDataTablePaginationData,
	ISort,
} from "@/js/types/default";
import DataTableFooter from "@/js/vue/components/DataTableFooter.vue";

const props = withDefaults(
	defineProps<{
		data: IDataTableDataTypes[];
		paginationData?: IDataTablePaginationData;
		columnData: IDataTableColumns[];
		currentPage?: number;
		itemsPerPage?: number;
		searchValue?: string;
		sortBy?: ISort;
		enableRowHover?: boolean;
		hideItemsPerPage?: boolean;
		placeholderText?: string;
		isRowClickable?: (row: IDataTableDataTypes) => boolean;
	}>(),
	{
		currentPage: 1,
		itemsPerPage: 20,
		paginationData: undefined,
		sortBy: undefined,
		searchValue: "",
		enableRowHover: true,
		hideItemsPerPage: false,
		placeholderText: "Search",
	},
);

const getInitialColumnData = () => {
	return props.columnData;
};

const localColumnData = ref<IDataTableColumns[]>(getInitialColumnData());

const visibleColumnsData = computed(() => {
	return localColumnData.value.filter(
		(localColumnDataItem) => localColumnDataItem.visible,
	);
});

const handleInternalRowClick = (selectedId: string) => {
	emit("handleRowClick", selectedId);
};

const handleHeaderSort = (key: string) => {
	if (props.sortBy?.handle === key) {
		emit("handleSortValue", {
			handle: key,
			variant: props.sortBy.variant === "DESC" ? "ASC" : "DESC",
		});
		return;
	}

	emit("handleSortValue", {
		handle: key,
		variant: "DESC",
	});
};

const emit = defineEmits<{
	handleSortValue: [value: ISort];
	handleRowClick: [id: string];

	handleSearchValue: [value: string];
	handleVisibleColumns: [title: string, isVisible: boolean];

	handleCurrentPage: [currentPage: number];
	handleItemsPerPage: [itemsPerPage: number];
}>();
</script>

<template>
	<!-- Table -->
	<table id="policies" class="table-striped table-hover table-borderless table">
		<thead>
			<tr>
				<th
					v-for="(column, columnIndex) in visibleColumnsData"
					:key="columnIndex"
					class="px-3 py-2"
					:style="column.sortable ? 'cursor:pointer;' : ''"
					:aria-sort="
						props.sortBy?.handle === column.key
							? props.sortBy?.variant === 'ASC'
								? 'ascending'
								: 'descending'
							: 'none'
					"
					@click="
						column.sortable && column.key
							? handleHeaderSort(column.key)
							: undefined
					"
				>
					<div class="d-flex align-items-center justify-content-between w-100">
						<span class="fw-bold">
							{{ column.title }}
						</span>

						<span
							v-if="column.sortable && column.key"
							class="d-inline-flex flex-column align-items-center lh-1"
						>
							<i
								class="ri-arrow-up-s-fill fs-5"
								:class="
									props.sortBy?.handle === column.key &&
									props.sortBy?.variant === 'ASC'
										? 'text-primary'
										: 'text-muted'
								"
							></i>
							<i
								class="ri-arrow-down-s-fill fs-5"
								:class="
									props.sortBy?.handle === column.key &&
									props.sortBy?.variant === 'DESC'
										? 'text-primary'
										: 'text-muted'
								"
							></i>
						</span>
					</div>
				</th>
			</tr>
		</thead>
		<tbody>
			<tr
				v-for="rowData in data"
				:key="rowData.id"
				class="border-accent-light border-b"
				:class="{
					'table-row--clickable':
						isRowClickable(rowData),
				}"
				@click="
					(isRowClickable(rowData)) &&
						handleInternalRowClick(rowData.id.toString())
				"
			>
				<td
					v-for="(column, columnIndex) in visibleColumnsData"
					:key="columnIndex"
					class="whitespace-nowrap px-6 py-3"
				>
					<slot :name="column.key" :row-data="rowData">
						{{ rowData[column.key] }}
					</slot>
				</td>
			</tr>
		</tbody>
	</table>

	<DataTableFooter
		v-if="paginationData"
		:items-per-page="itemsPerPage"
		:current-page="currentPage"
		:pagination-data="paginationData"
		:hide-items-per-page="hideItemsPerPage"
		@handle-items-per-page="
			(newItemsPerPage: number) => emit('handleItemsPerPage', newItemsPerPage)
		"
		@handle-current-page="
			(newPage: number) => emit('handleCurrentPage', newPage)
		"
	/>
</template>

<style scoped>
.table-row--clickable {
	cursor: pointer;
}
</style>
